import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDebug } from '../contexts/DebugContext';
import { useAuth } from '../contexts/AuthContext';
import { useAppHeaderHeight } from '../contexts/LayoutContext';
import api from '../services/api';
import JplModal from './JplModal';
import { can } from '../utils/permissions';
import { displayDate as fmtDate } from '../utils/dates';
import { PageHeader, Banner, WideSection, DebugPanel, JobPageLink } from './ui';
import InvoiceExtras from './InvoiceExtras';
// Display formatters, style tokens, the read-only task-line header/cells, and
// TimeDetailBlock are shared with the Invoice Approval screen via ./TaskLines.
import { approvalPill, ApprovalHistoryTable } from './ApprovalStatus';
import { money, hours, pct, isUnit, isLumpSum, th, thR, td, tdR, linkBtn, TaskDataHeaders, TaskDataCells, TimeDetailBlock } from './TaskLines';

// ---- formatting helpers local to this screen ----
// Lines the PM must give a value for (a Job Planning Line gets pre-created).
function needsValue(line) {
  return isUnit(line) || isLumpSum(line);
}
// Dollar amount a lump-sum % to invoice would actually bill now, mirroring NAV's
// CreateJobBillingLine: cumulative target (pct of Schedule Total Price) minus the
// amount already invoiced. Returns null when no valid percent is entered.
function lumpBillAmount(line, value) {
  const v = parseFloat(value);
  if (!Number.isFinite(v)) return null;
  return (v / 100) * (Number(line.ScheduleTotalPrice) || 0) - (Number(line.PreviouslyInvoicedAmount) || 0);
}

// Lump-sum % is cumulative: the entered % may not be below what's already been
// invoiced. Returns an error string for a checked lump-sum line that violates
// this, else null. A unit-billing line bills by UNITS (not a cumulative %), so
// the rule does not apply to it even when its task Type is Lump Sum — matching
// the Job Task Lines screen, where unit billing takes precedence over the % rule.
function lumpError(line, st) {
  if (!isLumpSum(line) || isUnit(line) || !st || !st.checked || st.dontInvoice) return null;
  if (st.value === '' || st.value === null || st.value === undefined) return null;
  // Parse both the same way the "prev. %" display does (parseFloat), so a value
  // that arrives as a formatted string still compares correctly.
  const v = parseFloat(st.value);
  const prev = parseFloat(line.PreviouslyInvoicedPct);
  if (Number.isNaN(v) || Number.isNaN(prev)) return null;
  if (v < prev - 0.001) return `% to invoice cannot be less than the ${pct(prev)} already invoiced (it is cumulative).`;
  return null;
}

// A task line the wizard shows by default:
//   lump sum        -> not already 100% billed
//   T&M / expense / charges (non-unit) -> has some unbilled amount
//   unit billing    -> always shown
function isNeeded(line) {
  // Lump sum / unit lines that already have an invoice (posted or unposted) this
  // month are hidden by default (still reachable via "Show All Task Lines").
  const invoicedThisMonth = Number(line.HasInvoiceThisMonth) === 1;
  if (isUnit(line)) return !invoicedThisMonth;
  if (isLumpSum(line)) return !invoicedThisMonth && Number(line.PreviouslyInvoicedPct) < 99.99;
  return Math.abs(Number(line.UnbilledAmount) || 0) > 0.005;
}

// ---- inline style tokens (th/thR/td/tdR/linkBtn imported from ./TaskLines) ----
const numInput = { width: 70, fontSize: 14 };
// Lump-sum "% to invoice" combo: a real pull-down of preset percents (5..100 by
// 5) that ALSO accepts a typed value not on the list. Replaces the native
// <datalist> input, whose dropdown flickered open/closed.
const PCT_OPTIONS = Array.from({ length: 20 }, (_, i) => (i + 1) * 5);
const comboWrap = { position: 'relative', display: 'inline-block' };
const comboInput = { ...numInput, paddingRight: 18, boxSizing: 'border-box' };
const comboCaret = { position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', fontSize: 10, color: 'var(--gray-500)', padding: 0, lineHeight: 1 };
const comboList = { position: 'absolute', top: '100%', left: 0, zIndex: 50, background: '#fff', border: '1px solid var(--gray-300)', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 180, overflowY: 'auto', minWidth: 70, marginTop: 2 };
const comboOption = { padding: '4px 10px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' };

// Percent pull-down with free-text entry for lump-sum "% to invoice".
function PercentCombo({ value, disabled, onChange }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(-1);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <span ref={wrapRef} style={comboWrap}>
      <input
        type="text" inputMode="numeric" className="form-control" style={comboInput}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
      />
      <button type="button" tabIndex={-1} disabled={disabled} aria-label="Choose a percent"
        style={comboCaret}
        onMouseDown={(e) => { e.preventDefault(); if (!disabled) setOpen((v) => !v); }}>▾</button>
      {open && !disabled && (
        <div style={comboList} role="listbox">
          {PCT_OPTIONS.map((n, i) => (
            <div key={n} role="option" aria-selected={hover === i} style={{ ...comboOption, background: hover === i ? 'var(--gray-100)' : undefined }}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(-1)}
              onMouseDown={(e) => { e.preventDefault(); onChange(String(n)); setOpen(false); }}>
              {n}%
            </div>
          ))}
        </div>
      )}
    </span>
  );
}
const pill = { fontSize: 11, background: 'var(--gray-200)', borderRadius: 10, padding: '1px 8px', color: 'var(--gray-700)' };
// Colored pill for the current-month invoice status shown on the job list.
function invStatusPill(status) {
  const map = {
    'Posted Invoice': { bg: '#dcfce7', fg: '#166534' },
    'Unposted Invoice': { bg: '#dbeafe', fg: '#1e40af' },
    'Unbilled': { bg: '#fef9c3', fg: '#854d0e' },
    'No T&M Activity': { bg: '#f3f4f6', fg: '#6b7280' },
    'Fully Billed': { bg: '#d1fae5', fg: '#065f46' },
    'No T&M Activity/Fully Billed': { bg: '#e5e7eb', fg: '#374151' },
  };
  const c = map[status] || { bg: '#f3f4f6', fg: '#374151' };
  return { fontSize: 11, background: c.bg, color: c.fg, borderRadius: 10, padding: '1px 8px', whiteSpace: 'nowrap' };
}

// jobNo: the job page's Create Invoices tab — that one job, without the list or
// Previous/Next.
export default function CreateInvoicesView({ deepLinkJob, jobNo, onOpenBudget } = {}) {
  const { debugMode } = useDebug();
  const { user } = useAuth();
  // Accounting Access users may list every active job, not just their PM jobs
  // (off by default).
  const canListAllJobs = can(user, 'createInvoicesAllJobs');
  const [allJobs, setAllJobs] = useState(false);
  // Sticky offset below the app header.
  const stickyTop = useAppHeaderHeight();

  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [error, setError] = useState('');
  const [navDebug, setNavDebug] = useState(null);

  const [view, setView] = useState('summary'); // 'summary' | 'wizard'
  const [currentJobNo, setCurrentJobNo] = useState('');

  const [tasks, setTasks] = useState([]);
  const [timeDetail, setTimeDetail] = useState({}); // jobTaskNo -> summarized rows[]
  const [pendingInvoices, setPendingInvoices] = useState([]); // unposted invoices for the job
  const [approvalHistory, setApprovalHistory] = useState({ invoices: {}, job: [] }); // Invoice Approval status history
  const [pendingError, setPendingError] = useState(''); // why the pending list failed to load
  const [rateWarnings, setRateWarnings] = useState([]); // unbilled T&M billed below default rate
  const [deletingDoc, setDeletingDoc] = useState(''); // DocNo currently being deleted
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [lineState, setLineState] = useState({}); // jobTaskNo -> { checked, value, dontInvoice }
  const [showAll, setShowAll] = useState(false);   // reveal fully-billed / no-unbilled lines
  const [comment, setComment] = useState('');
  const [creating, setCreating] = useState(false);
  const [banner, setBanner] = useState(null); // { kind: 'ok'|'err', text }
  const [jplModal, setJplModal] = useState(null); // { job, task } for the JPL pop-up

  // Jobs the wizard walks with Previous/Next: those that still have an open,
  // unbilled posting task line to bill (HasNeededTaskLine), PLUS any job with a
  // posted or unposted invoice created THIS month (InvoiceDocs non-empty). The
  // latter keeps a job in the walk after its invoice is created, so the PM can
  // Previous/Next back to it; jobs with nothing to bill and no invoice this
  // month are skipped.
  const walkJobs = useMemo(
    () => jobs.filter((j) => j.HasNeededTaskLine || parseInvoiceDocs(j.InvoiceDocs).length > 0),
    [jobs]
  );
  const currentJob = useMemo(
    () => jobs.find((j) => String(j.JobNo).trim() === currentJobNo) || null,
    [jobs, currentJobNo]
  );
  const walkPos = useMemo(
    () => walkJobs.findIndex((j) => String(j.JobNo).trim() === currentJobNo),
    [walkJobs, currentJobNo]
  );

  const captureDebug = useCallback((resp) => {
    if (debugMode && resp && resp._navDebug) setNavDebug(resp._navDebug);
  }, [debugMode]);

  // On a failed request, surface the full NAV round-trip into the debug panel
  // (the thrown error carries the server's response body, including _navDebug and
  // the raw NAV `data`) and return the best error message to show.
  const captureError = useCallback((e, fallback) => {
    const body = e && e.responseBody;
    if (debugMode && body && body._navDebug) setNavDebug(body._navDebug);
    let msg = (body && body.error) || (e && e.message) || fallback || 'Request failed';
    // Only expose the raw NAV `data` blob in the banner when debugging; otherwise
    // the server's `error` string is the whole user-facing message.
    if (debugMode && body && body.data && typeof body.data !== 'string') {
      try {
        const detail = JSON.stringify(body.data);
        if (detail && detail !== '{}' && !msg.includes(detail.slice(0, 40))) {
          msg += ` — ${detail.slice(0, 500)}`;
        }
      } catch (_) { /* ignore */ }
    }
    return msg;
  }, [debugMode]);

  const loadJobs = useCallback(async (silent) => {
    if (!silent) setLoadingJobs(true);
    setError('');
    try {
      const resp = await api.getCreateInvoiceJobs(debugMode, allJobs, jobNo);
      captureDebug(resp);
      const list = Array.isArray(resp.jobs) ? resp.jobs : [];
      setJobs(list);
      return list;
    } catch (e) {
      setError(captureError(e, 'Failed to load jobs'));
      return [];
    } finally {
      setLoadingJobs(false);
    }
  }, [debugMode, captureDebug, captureError, allJobs, jobNo]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Load a job's task lines and seed the per-line UI state.
  const loadTasks = useCallback(async (jobNo) => {
    setLoadingTasks(true);
    setTasks([]);
    setTimeDetail({});
    setPendingInvoices([]);
    setApprovalHistory({ invoices: {}, job: [] });
    setPendingError('');
    setRateWarnings([]);
    try {
      // Pending-invoice load must not fail the whole screen, but its error should
      // NOT be silently swallowed either (a missing TVF or a data issue would
      // otherwise show an empty section with no explanation).
      const [resp, sumResp, pendResp, rateResp] = await Promise.all([
        api.getCreateInvoiceTasks(jobNo, debugMode),
        api.getCreateInvoiceTimeSummary(jobNo, debugMode).catch(() => ({ detail: [] })),
        api.getJobPendingInvoices(jobNo, debugMode).catch((e) => ({ __err: e })),
        api.getCreateInvoiceRateWarnings(jobNo, debugMode).catch(() => ({ warnings: [] })),
      ]);
      captureDebug(resp);
      captureDebug(sumResp);
      captureDebug(rateResp);
      setRateWarnings(Array.isArray(rateResp.warnings) ? rateResp.warnings : []);
      if (pendResp && pendResp.__err) {
        setPendingError(captureError(pendResp.__err, 'Failed to load pending invoices'));
        setPendingInvoices([]);
      } else {
        captureDebug(pendResp);
        setPendingInvoices(Array.isArray(pendResp.invoices) ? pendResp.invoices : []);
        setApprovalHistory(pendResp.approvalHistory || { invoices: {}, job: [] });
      }
      const rows = Array.isArray(resp.tasks) ? resp.tasks : [];
      setTasks(rows);
      // Group the summarized employee/notes/hours detail by task line.
      const dmap = {};
      (Array.isArray(sumResp.detail) ? sumResp.detail : []).forEach((r) => {
        const k = String(r.JobTaskNo);
        if (!dmap[k]) dmap[k] = [];
        dmap[k].push(r);
      });
      setTimeDetail(dmap);
      const seed = {};
      rows.forEach((t) => {
        // Needed lines are checked by default; not-needed (fully billed / no
        // unbilled) lines start unchecked (they're hidden unless "Show All").
        // Unit lines default to 1 when auto.
        let value = '';
        // Unit lines default to 1 only when auto-billing AND there are units left
        // to invoice (Item 5 — don't pre-fill 1 when nothing remains).
        if (isUnit(t)) value = (Number(t.AutoUnitBillingIncrement) > 0 && Number(t.UnitsRemaining) > 0) ? '1' : '';
        seed[t.JobTaskNo] = { checked: isNeeded(t), value, dontInvoice: false };
      });
      setLineState(seed);
    } catch (e) {
      setBanner({ kind: 'err', text: captureError(e, 'Failed to load task lines') });
    } finally {
      setLoadingTasks(false);
    }
  }, [debugMode, captureDebug, captureError]);

  // Open a job in the wizard.
  const openJob = useCallback((jobNo) => {
    setBanner(null);
    setComment('');
    setShowAll(false);
    setCurrentJobNo(jobNo);
    setView('wizard');
    loadTasks(jobNo);
  }, [loadTasks]);

  // Deep link from the Budget tab: once jobs are loaded, open the named job's
  // wizard automatically (once).
  const deepLinkDone = useRef(false);
  const openFirst = deepLinkJob || jobNo;
  useEffect(() => {
    if (deepLinkDone.current || !openFirst || !jobs.length) return;
    const target = jobs.find((j) => String(j.JobNo).trim() === String(openFirst).trim());
    if (target) { deepLinkDone.current = true; openJob(String(target.JobNo).trim()); }
  }, [openFirst, jobs, openJob]);

  // Refresh the current job in place: re-pull the job list (aggregates may have
  // changed) and reload this job's task lines / pending invoices, without
  // changing the comment, Show-All, or which job is open.
  const refreshCurrent = useCallback(async () => {
    if (!currentJobNo) return;
    await loadJobs(true);
    await loadTasks(currentJobNo);
  }, [currentJobNo, loadJobs, loadTasks]);

  const gotoWalk = useCallback((delta) => {
    if (!walkJobs.length) { setView('summary'); return; }
    let next;
    if (walkPos === -1) next = delta > 0 ? 0 : walkJobs.length - 1;
    else next = walkPos + delta;
    if (next < 0 || next >= walkJobs.length) { setView('summary'); return; }
    openJob(String(walkJobs[next].JobNo).trim());
  }, [walkJobs, walkPos, openJob]);

  const setLine = (jobTaskNo, patch) => {
    setLineState((s) => ({ ...s, [jobTaskNo]: { ...s[jobTaskNo], ...patch } }));
  };

  // Visible lines: the "needed" ones, or all of them when Show All is on.
  const visibleTasks = useMemo(
    () => (showAll ? tasks : tasks.filter(isNeeded)),
    [tasks, showAll]
  );
  const hiddenCount = tasks.length - visibleTasks.length;

  // Toggle Bill for every visible line (header check-all / clear-all).
  const setAllVisible = (checked) => {
    setLineState((s) => {
      const next = { ...s };
      visibleTasks.forEach((t) => {
        next[t.JobTaskNo] = { ...next[t.JobTaskNo], checked, dontInvoice: checked ? false : next[t.JobTaskNo]?.dontInvoice };
      });
      return next;
    });
  };
  const allVisibleChecked = visibleTasks.length > 0 && visibleTasks.every((t) => lineState[t.JobTaskNo] && lineState[t.JobTaskNo].checked);

  const anyChecked = useMemo(
    () => visibleTasks.some((t) => lineState[t.JobTaskNo] && lineState[t.JobTaskNo].checked),
    [visibleTasks, lineState]
  );
  const hasLineErrors = useMemo(
    () => visibleTasks.some((t) => lumpError(t, lineState[t.JobTaskNo])),
    [visibleTasks, lineState]
  );

  const doCreate = useCallback(async () => {
    if (!currentJob) return;
    // Require an invoice comment (Item 3).
    if (!comment.trim()) {
      setBanner({ kind: 'err', text: 'Enter an invoice comment before creating the invoice.' });
      return;
    }
    // Hard limit of 2048 characters on the comment (codeunit parameter length).
    if (comment.length > 2048) {
      setBanner({ kind: 'err', text: `Comment section exceeds 2048 characters and is too long by ${comment.length - 2048} character${comment.length - 2048 === 1 ? '' : 's'}.` });
      return;
    }
    // Limit the comment to 10 lines (web-only rule).
    if ((comment.match(/\r\n|\r|\n/g) || []).length > 10) {
      setBanner({ kind: 'err', text: 'Comment cannot exceed 10 lines.' });
      return;
    }
    // Block if any checked lump-sum line is below its already-invoiced %.
    const badLine = tasks.find((t) => lumpError(t, lineState[t.JobTaskNo]));
    if (badLine) {
      setBanner({ kind: 'err', text: `Task ${badLine.JobTaskNo}: ${lumpError(badLine, lineState[badLine.JobTaskNo])}` });
      return;
    }
    // Validate values on checked lump-sum/unit lines up front.
    for (const t of tasks) {
      const st = lineState[t.JobTaskNo];
      if (st && st.checked && needsValue(t)) {
        if (st.value === '' || st.value === null || st.value === undefined || Number.isNaN(Number(st.value))) {
          setBanner({ kind: 'err', text: `Enter a ${isUnit(t) ? 'number of units' : 'percent'} to invoice for task ${t.JobTaskNo}.` });
          return;
        }
      }
    }
    const lines = tasks.map((t) => {
      const st = lineState[t.JobTaskNo] || {};
      return {
        jobTaskNo: t.JobTaskNo,
        checked: !!st.checked,
        taskType: t.TaskType,
        unitBilling: !!t.UnitBilling,
        value: st.value,
      };
    });

    // Capture the job now, before we refresh.
    const jobNo = currentJob.JobNo;
    const jobDesc = currentJob.JobDescription || '';

    setCreating(true);
    setBanner(null);
    try {
      const resp = await api.createInvoice(jobNo, lines, comment, debugMode);
      captureDebug(resp);
      // The invoiced amount comes from the server (the real Sales Invoice amount).
      // If it's missing, say so — that likely signals a problem, and we do NOT
      // substitute a web-app-computed figure.
      const successText = (resp && typeof resp.invoiceAmount === 'number')
        ? `Invoice creation for Job ${jobNo} - ${jobDesc} for ${money(resp.invoiceAmount)} was successful.`
        : `Invoice created for Job ${jobNo} - ${jobDesc}, but no amount was returned — this may indicate an issue.`;
      // Item 1: always stay on the job just invoiced and refresh it — the job
      // list (aggregates changed) and the job's task lines. When every line is
      // now invoiced the refreshed list is empty and shows the "No unbilled
      // posting task lines on this job" message. The comment was consumed by the
      // invoice just created, so clear it.
      await loadJobs(true);
      await loadTasks(jobNo);
      setComment('');
      // Set the green banner AFTER refreshing (loadTasks doesn't clear it, but
      // keep the ordering explicit) so it shows at the top of the reloaded screen.
      setBanner({ kind: 'ok', text: successText });
    } catch (e) {
      setBanner({ kind: 'err', text: captureError(e, 'Failed to create invoice') });
    } finally {
      setCreating(false);
    }
  }, [currentJob, tasks, lineState, comment, debugMode, captureDebug, captureError, loadJobs, loadTasks]);

  // Delete a pending (unposted) invoice draft, then refresh the screen's data
  // (task lines + job aggregates + the pending list all change).
  const deletePending = useCallback(async (docNo) => {
    if (!currentJob) return;
    const jobNo = currentJob.JobNo;
    if (!window.confirm(`Delete pending invoice ${docNo}?\n\nThis removes the draft invoice and returns its time to unbilled. This cannot be undone.`)) return;
    setDeletingDoc(docNo);
    setBanner(null);
    try {
      const r = await api.deletePendingInvoice(jobNo, docNo);
      // Refresh screen data (also reloads the pending list) and the job list.
      await Promise.all([loadTasks(jobNo), loadJobs(true)]);
      // Item 1: if the comment box is empty, carry the deleted invoice's comment
      // into it (so the text isn't lost when re-creating the invoice).
      let note = '';
      if (r && r.comment && !comment.trim()) {
        setComment(r.comment);
        note = ' Its comment was moved to the comment box.';
      }
      setBanner({ kind: 'ok', text: `Pending invoice ${docNo} was deleted.${note}` });
    } catch (e) {
      setBanner({ kind: 'err', text: captureError(e, 'Failed to delete the invoice') });
    } finally {
      setDeletingDoc('');
    }
  }, [currentJob, comment, loadTasks, loadJobs, captureError]);

  // ---- render ----
  if (loadingJobs) return <div style={{ padding: 24 }}>Loading jobs…</div>;
  if (error) {
    return (
      <div style={{ maxWidth: 640, margin: '24px auto', padding: '0 16px' }}>
        <Banner kind="err">{error}</Banner>
        <button className="btn btn-outline btn-sm" onClick={loadJobs}>Retry</button>
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 16px 32px' }}>
      {/* On the wizard the banner moves into the sticky header (so a rejection
          comment stays visible while scrolling); on the summary it stays here. */}
      {banner && view === 'summary' && <Banner kind={banner.kind} onClose={() => setBanner(null)}>{banner.text}</Banner>}
      {jobNo && view === 'summary'
        ? <div style={{ padding: 16 }}>{jobs.length ? 'Loading…' : `Job ${jobNo} is not one of your jobs to invoice.`}</div>
        : view === 'summary'
        ? <SummaryList jobs={jobs} walkCount={walkJobs.length} onOpen={openJob} onStart={() => gotoWalk(1)} onRefresh={() => loadJobs(true)}
            canListAllJobs={canListAllJobs} allJobs={allJobs} onAllJobs={setAllJobs} />
        : (
          <JobWizard
            job={currentJob}
            tasks={visibleTasks}
            timeDetail={timeDetail}
            pendingInvoices={pendingInvoices}
            approvalHistory={approvalHistory}
            rateWarnings={rateWarnings}
            pendingError={pendingError}
            deletingDoc={deletingDoc}
            onDeletePending={deletePending}
            onViewEntries={(jobNo, taskNo) => setJplModal({ job: jobNo, task: taskNo || '' })}
            onOpenBudget={onOpenBudget}
            banner={banner}
            onDismissBanner={() => setBanner(null)}
            stickyTop={stickyTop}
            loadingTasks={loadingTasks}
            lineState={lineState}
            setLine={setLine}
            comment={comment}
            setComment={setComment}
            creating={creating}
            anyChecked={anyChecked}
            hasLineErrors={hasLineErrors}
            onCreate={doCreate}
            onPrev={() => gotoWalk(-1)}
            onNext={() => gotoWalk(1)}
            onRefresh={refreshCurrent}
            onSummary={() => { setView('summary'); setBanner(null); }}
            walkPos={walkPos}
            walkTotal={walkJobs.length}
            single={!!jobNo}
            showAll={showAll}
            setShowAll={setShowAll}
            hiddenCount={hiddenCount}
            allVisibleChecked={allVisibleChecked}
            setAllVisible={setAllVisible}
          />
        )}

      <DebugPanel calls={navDebug} />

      {jplModal && (
        <JplModal
          job={jplModal.job}
          task={jplModal.task}
          onClose={() => setJplModal(null)}
        />
      )}
    </div>
  );
}

// Parse the JobList's InvoiceDocs ("<DocNo>:<isPosted>;…") into objects.
function parseInvoiceDocs(s) {
  return (s || '').split(';').map((x) => x.trim()).filter(Boolean).map((x) => {
    const i = x.lastIndexOf(':');
    return { docNo: i >= 0 ? x.slice(0, i) : x, isPosted: i >= 0 && x.slice(i + 1) === '1' };
  });
}
// Open an invoice: posted -> the real DocImage PDF (A/R logic); unposted -> the
// synthetic draft PDF. Same behavior as the View Entries invoice links.
async function openInvoiceDoc(docNo, isPosted, jobNo) {
  if (isPosted) {
    try {
      const r = await api.getInvoiceDocImage(docNo);
      if (r && r.link) window.open(r.link, '_blank', 'noopener');
      else alert(`No posted invoice document is on file for invoice ${docNo}.`);
    } catch (e) {
      alert((e && ((e.responseBody && e.responseBody.error) || e.message)) || 'Failed to open the invoice document.');
    }
  } else {
    window.open(api.getSyntheticInvoicePdfUrl(docNo, jobNo), '_blank', 'noopener');
  }
}

function SummaryList({ jobs, walkCount, onOpen, onStart, onRefresh, canListAllJobs, allJobs, onAllJobs }) {
  const [refreshing, setRefreshing] = useState(false);
  const doRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try { await onRefresh(); } finally { setRefreshing(false); }
  };
  return (
    <div>
      <PageHeader
        title="Create Invoices"
        onRefresh={doRefresh}
        refreshing={refreshing}
        actions={(
          <>
            {canListAllJobs && (
              <label style={{ fontSize: 13, color: 'var(--gray-600)', display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 6 }}>
                <input type="checkbox" checked={!!allJobs} onChange={(e) => onAllJobs(e.target.checked)} />
                All jobs
              </label>
            )}
            {walkCount > 0 && (
              <button className="btn btn-primary btn-sm" onClick={onStart}>Start wizard ({walkCount} job{walkCount === 1 ? '' : 's'})</button>
            )}
          </>
        )}
      />
      <p style={{ color: 'var(--gray-600)', fontSize: 14, marginTop: 0 }}>
        Your active jobs. Unbilled amounts match what a Create Invoice would bill. Click a job to review and invoice it.
      </p>
      {!jobs.length && <div style={{ padding: 16 }}>{allJobs ? 'No active jobs found.' : 'No active jobs found for you as Project Manager.'}</div>}
      {!!jobs.length && (
        <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Job</th>
              <th style={th}>Description</th>
              {allJobs && <th style={th}>Project Manager</th>}
              <th style={th}>Invoice Status (this mo.)</th>
              <th style={thR}>Unbilled (prior mo.)</th>
              <th style={thR}>Unbilled (total)</th>
              <th style={thR}>Approx Hrs (Last Mo.)</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => {
              const invDocs = parseInvoiceDocs(j.InvoiceDocs);
              // Most recent Invoice Approval status: per unposted invoice, or the
              // job-level flag when the job has no unposted invoice.
              const decisions = j.ApprovalDecisions || {};
              const hasUnposted = invDocs.some((d) => !d.isPosted);
              return (
              <tr key={j.JobNo} style={{ borderTop: '1px solid var(--gray-200)' }}>
                <td style={{ ...td, whiteSpace: 'nowrap' }}><button className="link-btn" onClick={() => onOpen(String(j.JobNo).trim())} style={linkBtn}>{j.JobNo}</button><JobPageLink jobNo={j.JobNo} /></td>
                <td style={td}>{j.JobDescription}</td>
                {allJobs && <td style={td}>{j.ProjectManagerName || j.ProjectManager}</td>}
                <td style={td}>
                  {j.InvoiceStatus ? <span style={invStatusPill(j.InvoiceStatus)}>{j.InvoiceStatus}</span> : ''}
                  {!hasUnposted && j.ApprovalJobFlag && (
                    <span style={{ ...approvalPill(j.ApprovalJobFlag), marginLeft: 4 }}>{j.ApprovalJobFlag}</span>
                  )}
                  {invDocs.length > 0 && (
                    <div style={{ marginTop: 3, fontSize: 12 }}>
                      {invDocs.map((d, i) => {
                        // No recorded status -> nothing shown.
                        const dec = !d.isPosted && decisions[d.docNo] && decisions[d.docNo].decision ? decisions[d.docNo] : null;
                        return (
                          <React.Fragment key={`${d.docNo}|${i}`}>
                            {i > 0 && ', '}
                            <button type="button" style={linkBtn}
                              title={d.isPosted ? 'Open posted invoice PDF' : 'Open draft (unposted) invoice PDF'}
                              onClick={() => openInvoiceDoc(d.docNo, d.isPosted, j.JobNo)}>{d.docNo}</button>
                            <InvoiceExtras invoiceNo={d.docNo} jobNo={j.JobNo} posted={d.isPosted} />
                            {dec && (
                              <span style={{ ...approvalPill(dec.decision), marginLeft: 4 }} title={dec.reason || ''}>{dec.decision}</span>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </td>
                <td style={tdR}>{money(j.UnbilledPriorMonth)}</td>
                <td style={tdR}>{money(j.UnbilledTotal)}</td>
                <td style={tdR}>{hours(j.ApproxHoursPriorMonth)}</td>
                <td style={td}>
                  {j.HasUnitBilling ? <span title="Has unit-billing lines" style={pill}>unit</span> : null}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function JobWizard({
  job, tasks, timeDetail, pendingInvoices, approvalHistory, rateWarnings, pendingError, deletingDoc, onDeletePending, onViewEntries, onOpenBudget, banner, stickyTop, loadingTasks, lineState, setLine, comment, setComment,
  creating, anyChecked, hasLineErrors, onCreate, onPrev, onNext, onRefresh, onSummary, walkPos, walkTotal, onDismissBanner,
  showAll, setShowAll, hiddenCount, allVisibleChecked, setAllVisible, single,
}) {
  // Height of the sticky job header, so the task-line column headers can stick
  // just below it.
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setHeaderH(el.offsetHeight));
    ro.observe(el);
    setHeaderH(el.offsetHeight);
    return () => ro.disconnect();
  }, [job]);
  // Which task lines have their time-detail summary expanded (default: expanded).
  const [collapsedDetail, setCollapsedDetail] = useState({});
  const commentEmpty = !comment || !comment.trim();
  // Comment has a hard limit of 2048 characters (the codeunit's parameter length).
  const COMMENT_MAX = 2048;
  const commentOver = (comment ? comment.length : 0) - COMMENT_MAX;
  const commentTooLong = commentOver > 0;
  // Comment is limited to 10 lines (web-only rule; Navision is unaffected).
  const COMMENT_MAX_LINES = 10;
  const commentLineBreaks = ((comment || '').match(/\r\n|\r|\n/g) || []).length;
  const commentTooManyLines = commentLineBreaks > COMMENT_MAX_LINES;
  // Collapse every line break into "; " so the comment fits on one line.
  const removeLineBreaks = () => {
    setComment(
      (comment || '')
        .split(/\r\n|\r|\n/)
        .map((s) => s.trim())
        .filter((s) => s !== '')
        .join('; ')
    );
  };

  // Item 1: copy all UNIQUE timesheet notes into the comment box, one per line —
  // no hours or person, and the same note shared by different people appears once.
  const copyAllNotes = () => {
    const seen = new Set();
    const notes = [];
    Object.values(timeDetail || {}).forEach((rows) => (rows || []).forEach((r) => {
      const n = (r.Notes || '').trim();
      if (!n) return;
      const key = n.toLowerCase().replace(/\s+/g, ' ');
      if (seen.has(key)) return;
      seen.add(key);
      notes.push(n);
    }));
    if (!notes.length) return;
    setComment((prev) => {
      const existing = (prev || '').replace(/\s+$/, '');
      const lower = existing.toLowerCase();
      const toAdd = notes.filter((n) => !lower.includes(n.toLowerCase()));
      if (!toAdd.length) return prev;
      return existing ? `${existing}\n${toAdd.join('\n')}` : toAdd.join('\n');
    });
  };
  const hasNotes = Object.values(timeDetail || {}).some((rows) => (rows || []).some((r) => (r.Notes || '').trim()));
  if (!job) return <div style={{ padding: 16 }}>Job not found. <button onClick={onSummary}>Back to list</button></div>;
  return (
    <div>
      {/* Sticky header: nav buttons, job name, header info, and the result/rejection
          banner stay visible while the (long) task list scrolls. */}
      {/* The box-shadow + clip-path extend the white background sideways across
          the screen, so wide task rows don't show beside it while scrolling. */}
      <div ref={headerRef} style={{ position: 'sticky', top: stickyTop, zIndex: 20, background: 'var(--card-bg, #fff)', boxShadow: '0 0 0 100vmax var(--card-bg, #fff)', clipPath: 'inset(0 -100vmax)', paddingTop: 4, paddingBottom: 6, borderBottom: '1px solid var(--gray-200)', marginBottom: 6 }}>
      <PageHeader
        title={single ? null : <>{job.JobNo} — {job.JobDescription}<JobPageLink jobNo={job.JobNo} /></>}
        onBack={single || creating ? undefined : onSummary}
        onPrev={single ? undefined : (creating ? null : onPrev)}
        onNext={single ? undefined : (creating ? null : onNext)}
        prevTitle="Previous job"
        nextTitle="Next job"
        position={!single && walkPos >= 0 && walkTotal > 0 ? `${walkPos + 1} of ${walkTotal}` : ''}
        onRefresh={onRefresh}
        refreshing={creating || loadingTasks}
        actions={(hiddenCount > 0 || showAll) && (
          <button className="btn btn-outline btn-sm" onClick={() => setShowAll((v) => !v)} disabled={creating}>
            {showAll ? 'Hide Extra Task Lines' : `Show All Task Lines${hiddenCount > 0 ? ` (+${hiddenCount})` : ''}`}
          </button>
        )}
      />
      <div style={{ color: 'var(--gray-600)', fontSize: 14 }}>
        {!single && <>{job.CustomerName || job.CustomerNo}{'  ·  '}</>}
        Unbilled total: {money(job.UnbilledTotal)}
        {'  ·  '}Approx Hrs (Last Mo.): {hours(job.ApproxHoursPriorMonth)}
        {'  ·  '}
        <button type="button" style={linkBtn} onClick={() => onViewEntries(String(job.JobNo).trim(), '')}>
          View all entries
        </button>
      </div>
      {banner && <Banner kind={banner.kind} onClose={onDismissBanner} style={{ margin: '8px 0 0' }}>{banner.text}</Banner>}
      {/* Unbilled T&M time billed below a person's default rate code (stays in
          the sticky header so it remains visible while the task lines scroll). */}
      {rateWarnings && rateWarnings.length > 0 && (
        <Banner kind="warn" style={{ margin: '8px 0 0' }}>
          {rateWarnings.map((w) => <div key={w.text}>{w.text}</div>)}
        </Banner>
      )}
      </div>

      {pendingError && <Banner kind="warn">Could not load pending invoices for this job: {pendingError}</Banner>}

      {!pendingError && pendingInvoices && pendingInvoices.length > 0 && (
        <div style={{ margin: '10px 0', padding: '8px 12px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0c4a6e', marginBottom: 4 }}>
            Pending (unposted) invoice{pendingInvoices.length === 1 ? '' : 's'} for this job
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Invoice</th>
                <th style={th}>Created Date</th>
                <th style={thR}>Amount</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {pendingInvoices.map((inv) => {
                const history = ((approvalHistory && approvalHistory.invoices) || {})[String(inv.DocNo).trim()] || [];
                return (
                <React.Fragment key={inv.DocNo}>
                <tr style={{ borderTop: '1px solid #e0f2fe' }}>
                  <td style={td}>
                    <button type="button" style={linkBtn} title="View draft (unposted) invoice PDF"
                      onClick={() => openInvoiceDoc(inv.DocNo, false, job.JobNo)}>{inv.DocNo}</button>
                    <InvoiceExtras invoiceNo={inv.DocNo} jobNo={job.JobNo} />
                  </td>
                  <td style={td}>{fmtDate(inv.PostingDate)}</td>
                  <td style={tdR}>{money(inv.Amount)}</td>
                  <td style={td}>
                    <button type="button" className="btn btn-outline btn-sm"
                      disabled={deletingDoc === inv.DocNo}
                      onClick={() => onDeletePending(inv.DocNo)}>
                      {deletingDoc === inv.DocNo ? 'Deleting…' : 'Delete'}
                    </button>
                  </td>
                </tr>
                {/* This invoice's Invoice Approval status history, newest first. */}
                {history.length > 0 && (
                  <tr>
                    <td colSpan={4} style={{ padding: '0 8px 6px 8px' }}>
                      <ApprovalHistoryTable entries={history} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Job-level Invoice Approval status since the most recent Clear. */}
      {approvalHistory && (approvalHistory.job || []).length > 0 && (
        <div style={{ margin: '10px 0', padding: '8px 12px', background: 'var(--gray-50, #f9fafb)', border: '1px solid var(--gray-200)', borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Job status</div>
          <ApprovalHistoryTable entries={approvalHistory.job} />
        </div>
      )}

      {loadingTasks ? (
        <div style={{ padding: 16 }}>Loading task lines…</div>
      ) : !tasks.length ? (
        // When there are hidden lines, this job DOES have open posting task
        // lines — they're just all billed, so none remain unbilled. Otherwise
        // the job genuinely has no open posting task lines.
        <div style={{ padding: 16 }}>
          {hiddenCount > 0
            ? 'No unbilled posting task lines on this job.'
            : 'No open posting task lines on this job.'}
        </div>
      ) : (
        // Task lines: up to the full screen width, but only as wide as needed
        // (centered); column headers stick below the job header while scrolling.
        <WideSection>
        <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
          <thead className="sticky-thead" style={{ '--sticky-top': `${stickyTop + headerH}px` }}>
            <tr>
              <th style={th}>
                <input
                  type="checkbox"
                  title="Check / clear all task lines"
                  checked={allVisibleChecked}
                  onChange={(e) => setAllVisible(e.target.checked)}
                /> Bill
              </th>
              <TaskDataHeaders />
              <th style={th}>To invoice</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => {
              const st = lineState[t.JobTaskNo] || { checked: true, value: '', dontInvoice: false };
              const err = lumpError(t, st);
              const detailRows = timeDetail[String(t.JobTaskNo)] || [];
              const detailOpen = detailRows.length > 0 && !collapsedDetail[t.JobTaskNo];
              return (
                <React.Fragment key={t.JobTaskNo}>
                <tr style={{ borderTop: '1px solid var(--gray-200)', background: err ? '#fef2f2' : undefined }}>
                  <td style={td}>
                    <input
                      type="checkbox"
                      checked={!!st.checked}
                      disabled={!!st.dontInvoice}
                      onChange={(e) => setLine(t.JobTaskNo, { checked: e.target.checked })}
                    />
                  </td>
                  <TaskDataCells t={t} jobNo={job.JobNo} onViewEntries={onViewEntries} onOpenBudget={onOpenBudget} />
                  <td style={td}>
                    {isUnit(t) ? (
                      <span>
                        <input
                          type="number" step="1" min="0" className="form-control" style={numInput}
                          disabled={!st.checked}
                          value={st.value}
                          onChange={(e) => setLine(t.JobTaskNo, { value: e.target.value })}
                        /> units
                      </span>
                    ) : isLumpSum(t) ? (
                      <span>
                        <PercentCombo
                          value={st.value}
                          disabled={!st.checked || st.dontInvoice}
                          onChange={(val) => setLine(t.JobTaskNo, { value: val })}
                        /> %
                        {lumpBillAmount(t, st.value) != null && (
                          <span style={{ marginLeft: 6, fontSize: 12, color: 'var(--gray-600)', whiteSpace: 'nowrap' }}>
                            ({money(lumpBillAmount(t, st.value))})
                          </span>
                        )}
                        <label style={{ marginLeft: 10, fontSize: 12, color: 'var(--gray-600)', whiteSpace: 'nowrap' }}>
                          <input
                            type="checkbox"
                            checked={!!st.dontInvoice}
                            onChange={(e) => setLine(t.JobTaskNo, { dontInvoice: e.target.checked, checked: e.target.checked ? false : true })}
                          /> Don&rsquo;t invoice
                        </label>
                        {err && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 2 }}>{err}</div>}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--gray-500)', fontSize: 12 }}>automatic</span>
                    )}
                  </td>
                </tr>
                {detailRows.length > 0 && (
                  <tr style={{ background: 'var(--gray-50, #f9fafb)' }}>
                    <td style={{ borderTop: 'none' }} />
                    <td colSpan={8} style={{ padding: '0 8px 8px 8px', borderTop: 'none' }}>
                      {/* width 0 / min-width 100%: long notes wrap instead of widening the table */}
                      <div style={{ width: 0, minWidth: '100%' }}>
                        <TimeDetailBlock
                          rows={detailRows}
                          open={detailOpen}
                          onToggle={() => setCollapsedDetail((c) => ({ ...c, [t.JobTaskNo]: !c[t.JobTaskNo] }))}
                        />
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </WideSection>
      )}

      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, color: 'var(--gray-600)', margin: 0 }}>
            Invoice comments (added to the invoice)
          </label>
          <button type="button" className="btn btn-outline btn-sm" onClick={copyAllNotes} disabled={!hasNotes}
            title="Copy all unique timesheet notes into the comment box (one per line)">
            Copy All Notes
          </button>
        </div>
        <textarea
          className="form-control"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          style={{
            maxWidth: 640,
            ...((commentTooLong || commentTooManyLines) ? { borderColor: '#b91c1c', boxShadow: '0 0 0 1px #b91c1c' } : {}),
          }}
          placeholder="Enter comments for this invoice…"
        />
        {commentTooLong && (
          <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 4, maxWidth: 640 }}>
            Comment section exceeds {COMMENT_MAX} characters and is too long by {commentOver} character{commentOver === 1 ? '' : 's'}.
          </div>
        )}
        {commentTooManyLines && (
          <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 4, maxWidth: 640, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>Comment cannot exceed {COMMENT_MAX_LINES} lines.</span>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={removeLineBreaks}
              title='Replace every line break with "; " to combine the comment onto fewer lines'
            >
              Remove all line breaks
            </button>
          </div>
        )}
      </div>

      <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn btn-primary" onClick={onCreate} disabled={creating || loadingTasks || !anyChecked || hasLineErrors || commentEmpty || commentTooLong || commentTooManyLines}>
          {creating ? 'Creating…' : 'Create Invoice'}
        </button>
        {hasLineErrors && (
          <span style={{ color: '#b91c1c', fontSize: 12 }}>Fix the highlighted lines before creating.</span>
        )}
        {!hasLineErrors && commentEmpty && anyChecked && (
          <span style={{ color: '#b91c1c', fontSize: 12 }}>Enter an invoice comment before creating.</span>
        )}
      </div>
    </div>
  );
}

// TimeDetailBlock is imported from ./TaskLines (shared with Invoice Approval).
